import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PostHogProvider } from 'posthog-js/react';
import { MapProvider } from '@vis.gl/react-maplibre';
import { Route as ReactRoute, Switch } from 'react-router-dom';

import { css } from '@emotion/react';
import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import React, { Suspense } from 'react';
// Using React.lazy instead of react-safe-lazy for React 19 compatibility
import { installAppConsole } from './app-console';
import { installChunkLoadErrorHandler } from './common/chunk-load-error-handler';
import { RedirectHandler } from './components/RedirectHandler';
import SentryErrorBoundary from './components/SentryErrorBoundary';
import { SettingsProvider } from './contexts/SettingsContext';
import { UserProvider } from './contexts/UserContext';
import { isEmbedded } from './common/widgets-utils';
/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css'; // ensure consistent rendering across browsers
import '@ionic/react/css/structure.css'; // ensure scrolling behaves like native
import '@ionic/react/css/typography.css'; // necessary for dynamic font scaling
import './theme/variables.css'; // theme variables

setupIonicReact();
installAppConsole();
installChunkLoadErrorHandler(); // Must be called before Sentry.init() to intercept errors first

const devToolCss = css({
  ['.tsqd-open-btn-container']: {
    top: '30px',
  },
});

function mobileFriendlySampleRate() {
  const conn = navigator.connection;
  const saveData = !!conn?.saveData; // user opted to reduce data usage
  const slowLink = ['slow-2g', '2g', '3g'].includes(conn?.effectiveType ?? 'unknown');

  // Reduced to 5-10% sampling to stay within Sentry span quota limits
  // At ~7K transactions/day with ~47 spans each, 10% sampling = ~1M spans/month
  // This provides sufficient statistical validity for performance analysis
  return saveData || slowLink ? 0.05 : 0.1;
}

const history = createBrowserHistory();
const isLocal = ['localhost', 'local.clarity.io'].includes(window.location.hostname);
const isProduction = process.env.NODE_ENV === 'production';

if (!isLocal) {
  const isDevString = localStorage.getItem('wlom:isDev') ?? 'false';
  const isDevUser = isDevString === 'true';
  const defaultSampleRate = mobileFriendlySampleRate();

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    release: 'openmap-ionic@' + import.meta.env.VITE_BUILD_NUMBER,
    integrations: [Sentry.reactRouterV5BrowserTracingIntegration({ history })],
    tunnel: '/proxy/monitoring',
    tracePropagationTargets: [/^\/api\//],
    tracesSampler() {
      // Log everything in DEV and RLS. Traffic is too low in these environments and
      // we don't want to miss anything.
      if (!isProduction) {
        return 1.0;
      }

      // Drop developer transactions
      if (isDevUser) {
        return 0.0;
      }

      return defaultSampleRate;
    },
    beforeSend(event) {
      // Ignore the known PostHog/rrweb Firefox iframe error.
      // We have verified that although an unhandled exception is thrown, it does not affect the app.
      // PostHog should eventually fix this, and a workaround would be difficult to implement in a way
      // that would not disable recording when it starts working again.
      // See https://clarity-movement.sentry.io/issues/6866397541
      if (
        event.exception?.values?.[0]?.type === 'NS_ERROR_UNEXPECTED' &&
        event.exception?.values?.[0]?.stacktrace?.frames?.some((frame) => frame.filename?.includes('@rrweb+record'))
      ) {
        return null; // Don't send to Sentry
      }
      return event;
    },
  });

  Sentry.setTag('isDev', isDevString);
}

const Route = isLocal ? ReactRoute : Sentry.withSentryRouting(ReactRoute);

// Lazy load all pages
const MapPage = React.lazy(() => import('./pages/MapPage'));
const CreateWidgetPage = React.lazy(() => import('./pages/CreateWidgetPage'));
const MapWidget = React.lazy(() => import('./widgets/MapWidget'));
const CurrentAQWidget = React.lazy(() => import('./widgets/CurrentAQWidget'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const BombPage = React.lazy(() => import('./pages/BombPage'));
const NetworkNotFoundPage = React.lazy(() => import('./pages/NetworkNotFoundPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const SelectNetworkPage = React.lazy(() => import('./pages/SelectNetworkPage'));
const NotificationPreferencesPage = React.lazy(() => import('./pages/NotificationPreferencesPage'));
const SchoolsWidget = React.lazy(() => import('./widgets/SchoolsWidget'));

// Create QueryClient outside of component to prevent recreation on HMR updates
const queryClient = new QueryClient();

const App: React.FC = () => {
  return (
    <IonApp>
      <SentryErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <SettingsProvider>
            <PostHogProvider
              apiKey={import.meta.env.VITE_POSTHOG_API_KEY}
              options={{
                api_host: import.meta.env.VITE_POSTHOG_HOST,
                session_recording: {
                  captureCanvas: {
                    // We are disabling this feature because it seems it is causing issues with Firefox
                    // See Sentry issue: https://clarity-movement.sentry.io/issues/6866397541
                    recordCanvas: !navigator.userAgent.includes('Firefox'),
                  },
                },
              }}
            >
              <MapProvider>
                {!isEmbedded && (
                  <div css={devToolCss}>
                    <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-right" />
                  </div>
                )}
                <UserProvider>
                  <IonReactRouter history={history}>
                    <Suspense fallback={<div>Loading...</div>}>
                      {/* 
                        We have been facing blank screens sometimes because the class ion-page-invisible is not
                        removed from the page when it loads. As a potential fix, we changed two things:
                        1. we moved Suspense outside of IonRouterOutlet as the docs suggest that having other
                           non-router components inside IonRouterOutlet can cause issues (see 
                           https://ionic-docs-5utg8ms4c-ionic1.vercel.app/docs/react/navigation#ionrouteroutlet).
                        2. we set animated={false} on IonRouterOutlet as we believe disabling animations might help.
                      */}
                      <IonRouterOutlet animated={false}>
                        <Switch>
                          {/* --- Widgets --- */}
                          <Route path="/:networkId/create-widget" exact component={CreateWidgetPage} />
                          <Route path="/:networkId/widgets/v1/map" exact component={MapWidget} />
                          <Route path="/:networkId/widgets/v1/current-air-quality" exact component={CurrentAQWidget} />
                          <Route path="/:networkId/widgets/v1/schools" exact component={SchoolsWidget} />
                          {/* --- SPA --- */}
                          <Route path="/login" exact component={LoginPage} />
                          {!isProduction && <Route path="/bomb" exact component={BombPage} />}
                          <Route path="/errors/network-not-found" exact component={NetworkNotFoundPage} />
                          <Route path="/errors/not-found" exact component={NotFoundPage} />
                          <Route path="/networks" exact component={SelectNetworkPage} />
                          <Route path="/notifications/preferences" exact component={NotificationPreferencesPage} />
                          <Route path="/:networkId" exact component={MapPage} />
                          <Route path="/:networkId/datasources/:datasourceId" exact component={MapPage} />
                          <Route path="/:networkId/ds/:datasourceId" exact component={MapPage} />
                          <Route path="/" exact component={() => <RedirectHandler url="/world" />} />
                          <Route component={() => <RedirectHandler url="/errors/not-found" />} /> {/* Fallback */}
                        </Switch>
                      </IonRouterOutlet>
                    </Suspense>
                  </IonReactRouter>
                </UserProvider>
              </MapProvider>
            </PostHogProvider>
          </SettingsProvider>
        </QueryClientProvider>
      </SentryErrorBoundary>
    </IonApp>
  );
};

// Accept HMR updates to prevent context loss during Fast Refresh
if (import.meta.hot) {
  import.meta.hot.accept();
}

export default App;
